<template>
  <div class="service-page">
    <!-- Carousel Hero Section -->
    <ServicesItsCarouselComp 
      :slides="autoServicesSlides"
      background-image="/resource/portfolio-01.jpg"
      pattern-image="/shapes/shape-06.png"
      :mission-vision="autoServicesMissionVision"
    />

    <!-- Automotive Excellence Section -->
    <ServiceItsCardProComp
      :header-data="automotiveExcellenceHeaderData"
      :excellence-data="automotiveExcellence"
      button-text="Learn More"
    />

    <!-- Advanced Technology Section -->
    <ServiceItsAdvancedTechComp
      :header-data="advancedTechHeaderData"
      :tech-features="techFeatures"
      tech-image="/images/resource/about-us.png"
      button-text="Explore Technology"
    />


    <!-- Auto Service Gallery -->
    <ServiceItsNormalGalleryComp
      :header-data="autoGalleryHeaderData"
      :gallery-data="autoGallery"
    />

    <!-- Locations Section -->
    <ServiceItsLocationComp
      :header-data="locationsHeaderData"
      :locations-data="locationsData"
    />

    <!-- Team Section -->
    <ServiceItsCertifiedComp
      :header-data="teamHeaderData"
      :team-stats="teamStatsData"
    />

    <!-- Why Sogdo for Vehicles Section -->
    <ServiceItsVehicleCardComp
      :header-data="whySogdoHeaderData"
      :vehicle-cards="whySogdoVehicleCards"
    />
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import WOW from 'wowjs'
import ServicesItsCarouselComp from '@/components/ServicesItsCarouselComp.vue'
import ServiceItsVehicleCardComp from '@/components/ServiceItsVehicleCardComp.vue'
import ServiceItsCertifiedComp from '@/components/ServiceItsCertifiedComp.vue'
import ServiceItsLocationComp from '@/components/ServiceItsLocationComp.vue'
import ServiceItsNormalGalleryComp from '@/components/ServiceItsNormalGalleryComp.vue'
import ServiceItsAdvancedTechComp from '@/components/ServiceItsAdvancedTechComp.vue'
import ServiceItsCardProComp from '@/components/ServiceItsCardProComp.vue'

export default {
  name: 'AutoServicesPage',
  components: {
    ServicesItsCarouselComp,
    ServiceItsVehicleCardComp,
    ServiceItsCertifiedComp,
    ServiceItsLocationComp,
    ServiceItsNormalGalleryComp,
    ServiceItsAdvancedTechComp,
    ServiceItsCardProComp
  },
  setup() {
    const automotiveExcellence = ref([
      {
        icon: 'fas fa-tools',
        title: 'Engine Services',
        description: 'Complete engine maintenance and repair services to keep your vehicle running at peak performance.',
        features: ['Engine Overhaul', 'Timing Belt', 'Valve Adjustment', 'Diagnostics'],
        detailedDescription: 'Our certified technicians provide comprehensive engine services using state-of-the-art equipment and genuine parts.'
      },
      {
        icon: 'fas fa-cogs',
        title: 'Transmission Services',
        description: 'Professional transmission maintenance and repair for both manual and automatic transmissions.',
        features: ['Transmission Rebuild', 'Clutch Replacement', 'Gearbox Repair', 'Fluid Service'],
        detailedDescription: 'Expert transmission services ensuring smooth gear shifting and optimal performance.'
      },
      {
        icon: 'fas fa-bolt',
        title: 'Electrical Systems',
        description: 'Advanced electrical diagnostics and repair for modern vehicle electronics and systems.',
        features: ['Battery Service', 'Alternator Repair', 'Starter Motor', 'Wiring Repairs'],
        detailedDescription: 'Comprehensive electrical system services for reliable vehicle operation.'
      },
      {
        icon: 'fas fa-car-side',
        title: 'Suspension & Steering',
        description: 'Complete suspension and steering system maintenance for safe and comfortable driving.',
        features: ['Shock Replacement', 'Strut Service', 'Steering Repair', 'Wheel Alignment'],
        detailedDescription: 'Professional suspension and steering services for optimal handling and safety.'
      },
      {
        icon: 'fas fa-wind',
        title: 'Exhaust Systems',
        description: 'Professional exhaust system repair and maintenance for optimal performance and emissions.',
        features: ['Muffler Replacement', 'Catalytic Converter', 'Pipe Repair', 'Emission Testing'],
        detailedDescription: 'Complete exhaust system services for performance and environmental compliance.'
      },
      {
        icon: 'fas fa-paint-brush',
        title: 'Body & Paint',
        description: 'Professional auto body repair and paint services to restore your vehicle\'s appearance.',
        features: ['Dent Repair', 'Paint Touch-up', 'Panel Replacement', 'Rust Treatment'],
        detailedDescription: 'Expert body and paint services to restore your vehicle to showroom condition.'
      }
    ])

    const techFeatures = ref([
      {
        icon: 'fas fa-microchip',
        title: 'Computer Diagnostics',
        description: 'Advanced computer diagnostic systems for accurate problem identification.'
      },
      {
        icon: 'fas fa-chart-line',
        title: 'Performance Analysis',
        description: 'Comprehensive performance analysis and optimization services.'
      },
      {
        icon: 'fas fa-shield-alt',
        title: 'Safety Systems',
        description: 'Complete safety system inspection and maintenance.'
      }
    ])

    const techOrbits = ref([
      { icon: 'fas fa-microchip', delay: '0s' },
      { icon: 'fas fa-chart-line', delay: '0.5s' },
      { icon: 'fas fa-shield-alt', delay: '1s' },
      { icon: 'fas fa-tools', delay: '1.5s' }
    ])

    const autoGallery = ref([
      {
        image: require('@/assets/resource/portfolio-01.jpg'),
        title: 'Engine Overhaul',
        description: 'Complete engine rebuild and performance optimization.',
        stats: [
          { value: '100%', label: 'Performance' },
          { value: '5 Years', label: 'Warranty' },
          { value: '24h', label: 'Service' }
        ]
      },
      {
        image: require('@/assets/resource/portfolio-02.jpg'),
        title: 'Transmission Service',
        description: 'Professional transmission repair and maintenance.',
        stats: [
          { value: 'Smooth', label: 'Shifting' },
          { value: '3 Years', label: 'Warranty' },
          { value: 'Same Day', label: 'Service' }
        ]
      },
      {
        image: require('@/assets/resource/portfolio-03.jpg'),
        title: 'Electrical Repair',
        description: 'Advanced electrical system diagnostics and repair.',
        stats: [
          { value: '100%', label: 'Reliability' },
          { value: '2 Years', label: 'Warranty' },
          { value: 'Expert', label: 'Technicians' }
        ]
      },
      {
        image: require('@/assets/resource/portfolio-04.jpg'),
        title: 'Body Restoration',
        description: 'Complete body repair and paint restoration.',
        stats: [
          { value: 'Showroom', label: 'Quality' },
          { value: '5 Years', label: 'Warranty' },
          { value: 'Custom', label: 'Colors' }
        ]
      }
    ])

    // Auto Services slides data
    const autoServicesSlides = ref([
      {
        subtitle: 'Vehicle Services',
        titleLines: [
          { text: 'India\'s Trusted Vehicle ', highlight: false },
          { text: 'Spare & Service ', highlight: 'Hub' }
        ],
        description: 'From Hero to Hyundai – we cover it all.',
        buttonText: 'Get Service',
        buttonLink: '/contact'
      },
      {
        subtitle: 'Vehicle Brands Covered',
        titleLines: [
          { text: 'Bikes & ', highlight: 'Cars' }
        ],
        description: '🛵 Bikes: Hero, Honda, Yamaha, TVS, Bullet Royal Enfield, HF Deluxe, Bajaj. 🚗 Cars: Maruti, Tata, Hyundai, Mahindra, Renault, Kia, Nissan, Toyota, Ford, MG, Skoda.',
        buttonText: 'Learn More',
        buttonLink: '/contact'
      },
      {
        subtitle: 'Our Services',
        titleLines: [
          { text: 'Complete ', highlight: 'Vehicle Care' }
        ],
        description: '🔧 Spare Parts Sales (All major brands), 🔁 Bike/Car Maintenance, 🏠 Home Pickup & Delivery, 🛠️ Installation Services.',
        buttonText: 'Book Now',
        buttonLink: '/contact'
      }
    ]);

    // Auto Services mission/vision data
    const autoServicesMissionVision = ref({
      vision: {
        title: 'Our Vision',
        description: 'To be the most trusted software innovation partner, building meaningful technology for real-world challenges.'
      },
      mission: {
        title: 'Our Mission',
        description: 'To build meaningful technology for real-world challenges through cutting-edge research, innovative development, and breakthrough discoveries that transform industries.'
      }
    });

    onMounted(async () => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false
      }).init()

      // Import and initialize Owl Carousel
      try {
        await import('owl.carousel')
        
        // Initialize Owl Carousel for banner
        setTimeout(() => {
          if (window.$ && window.$.fn.owlCarousel) {
            window.$('.banner-carousel').owlCarousel({
              loop: true,
              margin: 0,
              nav: true,
              active: true,
              smartSpeed: 1000,
              autoplay: 6000,
              navText: ['<span class="icon-05"></span>', '<span class="icon-06"></span>'],
              responsive: {
                0: {
                  items: 1
                },
                600: {
                  items: 1
                },
                768: {
                  items: 1
                },
                992: {
                  items: 1
                },
                1200: {
                  items: 1
                }
              }
            })
          }
        }, 100)
      } catch (error) {
        console.error('Error loading Owl Carousel:', error)
      }
    })

    // Why Sogdo for Vehicles header data
    const whySogdoHeaderData = {
      icon: "fas fa-star",
      tag: "WHY SOGDO",
      title: "Why Sogdo for Vehicles?",
      description: "Your trusted partner for all vehicle service needs with unmatched quality and value."
    }

    // Why Sogdo for Vehicles cards data
    const whySogdoVehicleCards = [
      {
        icon: "fas fa-check-circle",
        title: "OEM-Approved Spare Parts",
        description: "Genuine parts from authorized dealers ensuring quality and compatibility with your vehicle."
      },
      {
        icon: "fas fa-dollar-sign",
        title: "Transparent Pricing",
        description: "No hidden costs or surprise charges. Clear, upfront pricing for all services and parts."
      },
      {
        icon: "fas fa-shipping-fast",
        title: "Fast Delivery & Service",
        description: "Quick turnaround times with efficient service delivery and home pickup options."
      },
      {
        icon: "fas fa-percentage",
        title: "40% Less Cost with Champa Methodology",
        description: "Our proven methodology reduces service and development costs by 40% without compromising quality."
      }
    ]

    // Team Section header data
    const teamHeaderData = {
      icon: "fas fa-users",
      tag: "OUR TEAM",
      title: "Expert Team",
      description: "100+ Mechanics & Technicians. Certified & Trained Staff."
    }

    // Team stats data
    const teamStatsData = [
      {
        number: "100+",
        label: "Mechanics & Technicians"
      },
      {
        number: "Certified",
        label: "Trained Staff"
      },
      {
        number: "24/7",
        label: "Service Support"
      }
    ]

    // Locations Section header data
    const locationsHeaderData = {
      icon: "fas fa-map-marker-alt",
      tag: "OUR LOCATIONS",
      title: "Service Locations",
      description: "10+ Service Outlets Available in MP & Bihar. Expansion Coming Soon."
    }

    // Locations data
    const locationsData = [
      {
        icon: "fas fa-building",
        title: "Madhya Pradesh",
        description: "Multiple service centers across MP with certified technicians and genuine parts availability.",
        stats: ["5+ Centers", "50+ Technicians"]
      },
      {
        icon: "fas fa-building",
        title: "Bihar",
        description: "Expanding network in Bihar with modern facilities and comprehensive vehicle services.",
        stats: ["5+ Centers", "50+ Technicians"]
      }
    ]

    // Auto Gallery header data
    const autoGalleryHeaderData = {
      icon: "fas fa-wrench",
      tag: "SERVICE GALLERY",
      title: "Auto Service Showcase",
      description: "See our automotive transformations and service excellence"
    }

    // Advanced Technology Section header data
    const advancedTechHeaderData = {
      title: "Advanced Diagnostic Technology",
      description: "Our state-of-the-art diagnostic equipment and cutting-edge technology ensure accurate problem identification and efficient repairs."
    }

    // Automotive Excellence Section header data
    const automotiveExcellenceHeaderData = {
      icon: "fas fa-car",
      tag: "AUTOMOTIVE EXCELLENCE",
      title: "Premium Auto Care Solutions",
      description: "Experience the pinnacle of automotive service with our advanced technology and expert craftsmanship"
    }

    return {
      automotiveExcellence,
      techFeatures,
      techOrbits,
      autoGallery,
      autoServicesSlides,
      autoServicesMissionVision,
      whySogdoHeaderData,
      whySogdoVehicleCards,
      teamHeaderData,
      teamStatsData,
      locationsHeaderData,
      locationsData,
      autoGalleryHeaderData,
      advancedTechHeaderData,
      automotiveExcellenceHeaderData
    }
  }
}
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Automotive Excellence Section - Now handled by ServiceItsCardProComp component */

/* Advanced Technology Section - Now handled by ServiceItsAdvancedTechComp component */

/* Auto Gallery Section - Now handled by ServiceItsNormalGalleryComp component */

/* Custom button styling for carousel - More specific to avoid conflicts */
.service-page .banner__two .btn-box .theme-btn {
  padding: 12px 30px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  border-radius: 0 !important;
  background: linear-gradient(219deg, #FB0E76 0%, #910694 100%) !important;
  color: #fff !important;
  text-decoration: none !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 8px !important;
  transition: all 0.3s ease !important;
}

.service-page .banner__two .btn-box .theme-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 5px 15px rgba(251, 14, 118, 0.4) !important;
  color: #fff !important;
  text-decoration: none !important;
}

.service-page .banner__two .btn-box .theme-btn i {
  font-size: 12px !important;
  transition: transform 0.3s ease !important;
}

.service-page .banner__two .btn-box .theme-btn:hover i {
  transform: translateX(3px) !important;
}

/* Mission/Vision block styling */
.banner__mission__block {
  border-radius: 10px 0px 0px 0px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin-top: 20px;
}

.mission__block {
  padding: 40px 50px;
  border-right: 1px solid rgba(255, 255, 255, 0.10);
  flex: 1;
}

.mission__block:last-child {
  border-right: none;
}

.mission__block h3 {
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.mission__block p {
  color: #ccc;
  font-size: 14px;
  line-height: 24px;
  margin: 0;
  font-family: 'Open Sans', sans-serif;
}

/* Locations Section - Now handled by ServiceItsLocationComp component */

/* Team Section - Now handled by ServiceItsCertifiedComp component */

/* Why Sogdo for Vehicles Section - Now handled by ServiceItsVehicleCardComp component */

/* Reduce banner height - More specific to avoid conflicts */
.service-page section.banner__two {
  padding-top: 100px !important;
  padding-bottom: 100px !important;
}

.service-page .banner__two .content-box {
  padding-bottom: 60px !important;
}

/* Responsive adjustments - More specific to avoid conflicts */
@media (max-width: 768px) {
  .service-page .banner__two .content-box h2 {
    font-size: 50px !important;
    line-height: 60px !important;
  }
  
  .service-page .mission__block {
    padding: 30px 25px !important;
  }
  
  .service-page .mission__block h3 {
    font-size: 18px !important;
  }
  
  .service-page .mission__block p {
    font-size: 13px !important;
    line-height: 22px !important;
  }
}

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Global Styles */
body {
  font-family: 'Open Sans', sans-serif;
  line-height: 1.6;
  color: #333;
}

.see__pad {
  padding: 100px 0;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

/* Pattern Layers */
.pattern-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.pattern-1 {
  top: 10%;
  right: 5%;
  width: 100px;
  height: 100px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-2 {
  bottom: 20%;
  left: 8%;
  width: 80px;
  height: 80px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-3 {
  top: 30%;
  left: 10%;
  width: 120px;
  height: 120px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-4 {
  top: 15%;
  right: 15%;
  width: 90px;
  height: 90px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-5 {
  bottom: 25%;
  right: 20%;
  width: 70px;
  height: 70px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-6 {
  top: 40%;
  left: 5%;
  width: 110px;
  height: 110px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.09;
}

.pattern-7 {
  bottom: 15%;
  left: 12%;
  width: 85px;
  height: 85px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-8 {
  top: 35%;
  right: 8%;
  width: 95px;
  height: 95px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

/* Intro Section */
.service-intro {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.service-intro-content {
  position: relative;
  z-index: 2;
}

.service-section-label h4 {
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 13.2px;
  color: #F6EC4F;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
  text-transform: uppercase;
}

.service-main-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.service-description p {
  font-size: 18px;
  color: #fff;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

/* Services Offered Section */
.services-offered {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.services-header {
  position: relative;
  z-index: 2;
  margin-bottom: 60px;
}

.services-section-label h4 {
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 13.2px;
  color: #F6EC4F;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
  text-transform: uppercase;
}

.services-main-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.services-description p {
  font-size: 18px;
  color: #fff;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

.service-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(246, 236, 79, 0.1);
  border-radius: 15px;
  padding: 40px 30px;
  height: 100%;
  position: relative;
  transition: all 0.3s ease;
  overflow: hidden;
}

.service-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(246, 236, 79, 0.2);
  border-color: #F6EC4F;
}

.service-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.block-shape-1 {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, rgba(246, 236, 79, 0.1), rgba(246, 236, 79, 0.05));
  border-radius: 50%;
  opacity: 0.3;
}

.block-shape-2 {
  position: absolute;
  bottom: -30px;
  left: -30px;
  width: 80px;
  height: 80px;
  background: linear-gradient(45deg, rgba(246, 236, 79, 0.08), rgba(246, 236, 79, 0.03));
  border-radius: 50%;
  opacity: 0.4;
}

.service-content {
  position: relative;
  z-index: 2;
}

.service-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #F6EC4F, #E6D43A);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
}

.service-icon i {
  font-size: 24px;
  color: #00061A;
}

.service-content h3 {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.service-content p {
  font-size: 16px;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.6;
}

.service-content ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.service-content ul li {
  color: #fff;
  font-size: 14px;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.service-content ul li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #F6EC4F;
  font-weight: bold;
}

/* Call to Action Section */
.service-cta {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.cta-content {
  position: relative;
  z-index: 2;
}

.cta-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.cta-description p {
  font-size: 18px;
  color: #fff;
  max-width: 600px;
  margin: 0 auto 40px;
  line-height: 1.8;
}

.cta-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.primary-btn {
  background: linear-gradient(135deg, #F6EC4F, #E6D43A);
  color: #00061A;
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(246, 236, 79, 0.3);
}

.secondary-btn {
  background: transparent;
  color: #F6EC4F;
  border-color: #F6EC4F;
}

.secondary-btn:hover {
  background: #F6EC4F;
  color: #00061A;
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 767px) {
  .service-main-title h2,
  .services-main-title h2,
  .cta-title h2 {
    font-size: 36px;
    line-height: 46px;
  }
  
  .service-section-label h4,
  .services-section-label h4 {
    letter-spacing: 8px;
    font-size: 16px;
  }
  
  .service-item {
    padding: 30px 20px;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .cta-btn {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
}

@media (max-width: 399px) {
  .see__pad {
    padding: 60px 0;
  }
  
  .service-main-title h2,
  .services-main-title h2,
  .cta-title h2 {
    font-size: 28px;
    line-height: 38px;
  }
}
</style> 